import useModals from '../../../hooks/Common/useModals';
import { CommonModal } from '../../Common/CommonModal';
import CommonReport from '../../Common/CommonReport';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import CommonPlayer from '../../Common/CommonPlayer';
import { useEffect, useRef, useState } from 'react';
import { lfShelvesStatusList } from '../../../web_api/models';
import lfShelvesStatus from '../../../web_api/methods/fruits-vegetables/lfShelvesStatus';
import LoadingPage from '../../../layout/LoadingPage';
import getAzureFile from '../../../web_api/methods/files/getAzureFile';
import defaultBakery from '../../../assets/images/default_bakery.jpg';

const FruitsVegetablesImages = () => {
  const params = useParams();
  const locationId = Number(params.locationId);
  const { show, toggleModal } = useModals([false]);
  const cachedLocationsList = useSelector((state: RootState) => state.cachedLocations);
  const currentLocation = cachedLocationsList.list.find((location) => location.id === locationId);
  const [playerProgress, setPlayerProgress] = useState(0);
  const [timeoutIds, setTimeoutIds] = useState<NodeJS.Timeout>();
  const [loading, setLoading] = useState(false);
  const [imageArray, setImageArray] = useState<string[]>([]);
  const activeLocation = useSelector((state: RootState) => state.activeLocation);
  const toolbarHourFilter = useSelector((state: RootState) => state.toolbarHourFilter);
  const toolbarDateFilter = useSelector((state: RootState) => state.toolbarDateFilter);
  const tenantKey = useSelector((state: RootState) => state.user.tenant_key);
  const [currentImage, setCurrentImage] = useState<string>('');
  const username = useSelector((state: RootState) => state.user.user_name);
  const requestIdRef = useRef(0);
  const imagesByIndexRef = useRef<(string | null)[]>([]);
  const imageArrayRef = useRef<string[]>([]);
  const hasSetFirstImageRef = useRef(false);
  const currentIndexRef = useRef(0);
  const autoPlayRef = useRef(false);

  const isDemoUser = () => username.includes('demo');

  const loadShelveStatus = async () => {
    try {
      const requestId = (requestIdRef.current += 1);
      setLoading(true);
      setImageArray([]);
      setCurrentImage('');
      hasSetFirstImageRef.current = false;
      imagesByIndexRef.current = [];
      autoPlayRef.current = false;
      setPlayerProgress(0);
      if (timeoutIds) {
        clearTimeout(timeoutIds);
        setTimeoutIds(undefined);
      }
      if (!isDemoUser()) {
        let lfShelvesStatusRes = await lfShelvesStatus(
          parseInt(activeLocation.id),
          new Date(toolbarDateFilter.date),
          parseInt(toolbarHourFilter.hour),
          parseInt(toolbarHourFilter.toHour)
        );

        lfShelvesStatusRes.sort((a, b) => a.hour - b.hour);

        if (requestId !== requestIdRef.current) return;

        let uniqueFilenames = new Set<string>();
        let filenames: string[] = [];

        for (const item of lfShelvesStatusRes) {
          if (item.filename && !uniqueFilenames.has(item.filename)) {
            uniqueFilenames.add(item.filename);
            filenames.push(item.filename);
          }
        }

        if (filenames.length === 0) {
          setLoading(false);
          return;
        }

        imagesByIndexRef.current = new Array(filenames.length).fill(null);

        let pending = filenames.length;

        filenames.forEach((filename, index) => {
          getAzureFile(filename, { returnEmptyOnError: false })
            .then((newImage) => {
              if (requestId !== requestIdRef.current) return;

              imagesByIndexRef.current[index] = newImage;

              const availableImages = imagesByIndexRef.current.filter((img): img is string => Boolean(img));
              imageArrayRef.current = availableImages;
              setImageArray(availableImages);

              if (!hasSetFirstImageRef.current) {
                hasSetFirstImageRef.current = true;
                setCurrentImage(newImage);
                setLoading(false);
                if (!autoPlayRef.current) {
                  autoPlayRef.current = true;
                  startPlayer();
                }
              }
            })
            .catch((error) => {
              if (requestId !== requestIdRef.current) return;
              console.error(`Failed to fetch image: ${filename}`, error);
            })
            .finally(() => {
              if (requestId !== requestIdRef.current) return;
              pending -= 1;
              if (pending === 0 && !hasSetFirstImageRef.current) {
                setLoading(false);
              }
            });
        });
      } else {
        const defaultImgArray = Array(12).fill(defaultBakery);

        imageArrayRef.current = defaultImgArray;
        setCurrentImage(defaultImgArray[0]);
        setImageArray(defaultImgArray);
        setLoading(false);
        if (!autoPlayRef.current) {
          autoPlayRef.current = true;
          startPlayer();
        }
      }
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  };

  useEffect(() => {
    loadShelveStatus();
    // setCurrentImage('https://www.pixelstalk.net/wp-content/uploads/2016/07/1080p-Full-HD-Images.jpg')
    // setImageArray(['https://www.pixelstalk.net/wp-content/uploads/2016/07/1080p-Full-HD-Images.jpg', 'https://bloggertowp.org/wp-content/uploads/sites/9/2014/04/nature-lake-full-hd-wallpaper.jpg', 'https://wallpaperaccess.com/full/138728.jpg'])
  }, [activeLocation.id, toolbarDateFilter.date, toolbarHourFilter]);

  useEffect(() => {
    imageArrayRef.current = imageArray;
  }, [imageArray]);

  const startPlayer = () => {
    const images = imageArrayRef.current;
    if (images.length === 0) return;

    let startIndex = images.indexOf(currentImage);
    if (startIndex === -1) startIndex = 0;
    currentIndexRef.current = startIndex;

    let timeoutId;

    const setNextImage: () => NodeJS.Timeout | undefined = () => {
      const currentImages = imageArrayRef.current;

      if (currentImages.length === 0) {
        setPlayerProgress(0);
        setTimeoutIds(undefined);
        return;
      }

      if (currentIndexRef.current >= currentImages.length) {
        setCurrentImage(currentImages[0]);
        setPlayerProgress(0);
        setTimeoutIds(undefined);
        return;
      }

      setCurrentImage(currentImages[currentIndexRef.current]);
      currentIndexRef.current += 1;
      setPlayerProgress((currentIndexRef.current / currentImages.length) * 100);

      timeoutId = setTimeout(setNextImage, 1500);

      setTimeoutIds(timeoutId);
      return timeoutId;
    };
    timeoutId = setNextImage();
  };

  const stopPlayer = () => {
    if (timeoutIds) {
      clearTimeout(timeoutIds);
      setTimeoutIds(undefined);
    }
  };

  const handlePlayerClick = () => {
    if (timeoutIds) {
      stopPlayer();
    } else {
      startPlayer();
    }
  };

  return (
    <>
      <div className='mb-5 d-flex justify-content-center'>
        <div className='w-100 bg-body rounded'>
          <CommonReport leftTitle={`Shelves`}>
            {!loading ? (
              <>
                <div className='px-2'>
                  <img className='w-100 img-fluid rounded' src={currentImage} />
                </div>

                {/* <AlertCanvasComponent

                                            // imageUrl={`https://img.freepik.com/free-photo/waist-up-portrait-handsome-serious-unshaven-male-keeps-hands-together-dressed-dark-blue-shirt-has-talk-with-interlocutor-stands-against-white-wall-self-confident-man-freelancer_273609-16320.jpg?size=626&ext=jpg`}
                                            imageUrl={`/alerts-media/${tenantKey}/${new Date().getFullYear()}/${new Date().getMonth() + 1}/${currentImage}`}
                                            actualHeight={3840}
                                            actualWidth={2160}

                                            canvasHeight={"500px"}
                                            canvasWidth={"100%"}
                                            multiple_bboxes={[]} big={true} /> */}
                <div className='mt-5'>
                  <CommonPlayer className='pe-2' handlePlayerClick={handlePlayerClick} playerProgress={playerProgress} timeoutIds={timeoutIds} />
                </div>
              </>
            ) : (
              <LoadingPage />
            )}
          </CommonReport>
        </div>
      </div>

      <CommonModal
        title={currentLocation?.name || 'last status'}
        size='xl'
        show={show[0]}
        showModalCallback={() => toggleModal(0)}
        closeButton={true}
        confirmButton={true}
        bodyClass='flex-column w-100 ps-15 pe-15 pb-10'
        closeButtonOptions={{
          icon: 'bi bi-x',
          variant: 'secondary',
          class: '',
        }}
        confirmButtonOptions={{
          icon: 'bi bi-trash3',
          variant: '',
          class: 'd-none',
        }}
      >
        <img className='w-100 img-fluid rounded' src={currentImage} />
      </CommonModal>
    </>
  );
};

export default FruitsVegetablesImages;
